/**
 * Preview and Payment Component
 */

import React from 'react';
import { FormData, PricingInfo } from '../types';
import { calculateTotalAssets, calculateTotalLiabilities, calculateNetAssets, formatCurrency } from '../utils/helpers';

interface PreviewAndPaymentProps {
  formData: FormData;
  pricing: PricingInfo | null;
  onPayment: () => void;
  loading: boolean;
  onBack: () => void;
}

const PreviewAndPayment: React.FC<PreviewAndPaymentProps> = ({
  formData,
  pricing,
  onPayment,
  loading,
  onBack,
}) => {
  const totalAssets = calculateTotalAssets(formData.assets);
  const totalLiabilities = calculateTotalLiabilities(formData.liabilities);
  const netAssets = calculateNetAssets(formData.assets, formData.liabilities);

  const isMultiState = formData.multiState || false;
  const price = pricing ? (isMultiState ? pricing.multiState.price : pricing.singleState.price) : 29;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Review & Payment
      </h2>

      {/* Form Summary Badge */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex items-center">
          <span className="text-blue-600 text-2xl mr-3">📋</span>
          <div>
            <h3 className="font-semibold text-blue-800">Form Submitted</h3>
            <p className="text-sm text-blue-700">
              Your information has been saved. Complete payment to view your property division results
              and receive the detailed PDF report via email.
            </p>
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Financial Summary</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600">Total Assets</div>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalAssets)}</div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="text-sm text-gray-600">Total Liabilities</div>
            <div className="text-2xl font-bold text-red-600">{formatCurrency(totalLiabilities)}</div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm">
          <div className="text-sm text-gray-600 mb-1">Net Assets (Assets - Liabilities)</div>
          <div className="text-3xl font-bold text-blue-600">{formatCurrency(netAssets)}</div>
        </div>
      </div>

      {/* Information Summary */}
      <div className="bg-white border-2 border-gray-200 p-6 rounded-lg mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Your Information</h3>

        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">State</span>
            <span className="font-semibold">{formData.state}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Your Income</span>
            <span className="font-semibold">{formatCurrency(formData.userIncome)}/year</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Spouse Income</span>
            <span className="font-semibold">{formatCurrency(formData.spouseIncome)}/year</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Children</span>
            <span className="font-semibold">{formData.hasChildren ? `${formData.numberOfChildren || 0}` : 'None'}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-gray-600">Email</span>
            <span className="font-semibold text-sm">{formData.email}</span>
          </div>
        </div>
      </div>

      {/* What You'll Get */}
      <div className="bg-green-50 p-4 rounded-lg mb-6">
        <h3 className="font-semibold text-green-900 mb-3">📄 Your Complete Report Will Include:</h3>
        <ul className="text-sm text-green-800 space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-green-600">✓</span>
            <span>Executive summary with detailed breakdown</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600">✓</span>
            <span>Step-by-step calculation process with explanations</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600">✓</span>
            <span>State-specific legal information and references</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600">✓</span>
            <span>Analysis of all factors affecting the division</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600">✓</span>
            <span>Complete legal disclaimers and guidance</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-green-600">✓</span>
            <span>Professional PDF format for easy sharing</span>
          </li>
        </ul>
      </div>

      {/* Pricing and Payment */}
      <div className="bg-white border-2 border-blue-200 p-6 rounded-lg">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {isMultiState ? 'Multi-State Report' : 'Property Division Report'}
            </h3>
            <p className="text-sm text-gray-600">
              {isMultiState ? 'Compare across multiple states' : 'For ' + formData.state}
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">${price}</div>
            <div className="text-sm text-gray-500">One-time payment</div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 text-sm text-green-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Secure payment via Stripe</span>
        </div>

        <div className="flex items-center gap-2 mb-6 text-sm text-gray-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Report delivered to your email within 5-10 minutes</span>
        </div>

        <button
          onClick={onPayment}
          disabled={loading}
          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-4 px-6 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-lg shadow-lg"
        >
          {loading ? 'Processing...' : `Pay $${price} - Get Your Report`}
        </button>

        <div className="mt-4 text-center text-xs text-gray-500">
          <p>By clicking above, you agree to our Terms of Service and Privacy Policy.</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="mt-6 flex justify-between">
        <button
          onClick={onBack}
          className="bg-gray-200 text-gray-700 font-semibold py-3 px-8 rounded-lg hover:bg-gray-300 transition duration-300"
        >
          Back
        </button>
      </div>

      {/* Final Disclaimer */}
      <div className="mt-8 text-center text-xs text-gray-600 bg-gray-50 p-4 rounded-lg">
        <p className="font-semibold mb-2">Legal Disclaimer</p>
        <p>
          This calculator provides estimates based on general legal principles and does not
          constitute legal advice. Actual court decisions may vary significantly based on
          specific circumstances not captured in this calculator. You should consult with
          a qualified family law attorney before making any decisions regarding property division.
        </p>
      </div>
    </div>
  );
};

export default PreviewAndPayment;
